import { Component, HostListener, Input, inject } from '@angular/core';
import { ScorecardDisplayType, ScorecardV2 } from '@fgb/core';
import { ScorecardsV2DescriptionService } from '../scorecards-v2-description.service';

@Component({
  selector: 'fgbcl-scorecards-v2-cards',
  templateUrl: './scorecards-v2-cards.component.html',
  styleUrl: './scorecards-v2-cards.component.scss',
})
export class ScorecardsV2CardsComponent {
  @Input({ required: true }) scorecard: ScorecardV2;
  /** Next scorecard in the chain, shown locked inside the footer until this one is completed */
  @Input() linkedChild?: ScorecardV2;
  @Input() hideLocked = false;

  private descriptionService = inject(ScorecardsV2DescriptionService);
  private pointerStartX = 0;

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    this.pointerStartX = event.clientX;
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    // Ignore the click that ends a horizontal drag
    if (Math.abs(event.clientX - this.pointerStartX) > 5) return;
    this.descriptionService.open(this.scorecard.ScorecardId);
  }

  isProgressType(scorecard: ScorecardV2): boolean {
    return [
      ScorecardDisplayType.punchcardView,
      ScorecardDisplayType.progressBarView,
      ScorecardDisplayType.singleAction,
    ].includes(scorecard.ScoreCardDisplayType);
  }

  isSoloAction(scorecard: ScorecardV2): boolean {
    return scorecard.ScoreCardDisplayType === ScorecardDisplayType.soloAction;
  }

  /** Name of the solo scorecard's single action, falling back to the scorecard text when it has none */
  getSoloActionName(scorecard: ScorecardV2): string {
    return scorecard.Actions?.[0]?.Content?.[0]?.Name || scorecard.ShortDescription;
  }

  isMilestoneType(scorecard: ScorecardV2): boolean {
    return scorecard.ScoreCardDisplayType === ScorecardDisplayType.milestoneProgressBarView;
  }

  isCounterType(scorecard: ScorecardV2): boolean {
    return scorecard.ScoreCardDisplayType === ScorecardDisplayType.numberView;
  }

  getProgressPercentage(scorecard: ScorecardV2): number {
    if (!scorecard.TotalToComplete) return 0;
    return Math.floor((scorecard.ActionsCompleted / scorecard.TotalToComplete) * 100);
  }

  getMaxMilestone(scorecard: ScorecardV2): number {
    const milestones = scorecard.Milestone?.Milestones;
    if (!milestones?.length) return 0;
    return Math.max(...milestones.map((m) => m.Value));
  }

  getMilestonesSorted(scorecard: ScorecardV2) {
    const milestones = scorecard.Milestone?.Milestones;
    if (!milestones?.length) return [];
    return [...milestones].sort((a, b) => a.Value - b.Value);
  }

  isMilestoneReached(scorecard: ScorecardV2, value: number): boolean {
    return (scorecard.Milestone?.CustomerValue ?? 0) >= value;
  }

  /** Fill percentage of the segment leading up to the milestone at the given index of the sorted list */
  getMilestoneSegmentFill(scorecard: ScorecardV2, index: number): number {
    const milestones = this.getMilestonesSorted(scorecard);
    const end = milestones[index]?.Value ?? 0;
    const start = index > 0 ? milestones[index - 1].Value : 0;
    if (end <= start) return this.isMilestoneReached(scorecard, end) ? 100 : 0;
    const progress = ((scorecard.Milestone?.CustomerValue ?? 0) - start) / (end - start);
    return Math.min(100, Math.max(0, progress * 100));
  }

  isHeaderLocked(scorecard: ScorecardV2): boolean {
    return scorecard.Locked && !this.linkedChild;
  }

  onChildClick(event: Event): void {
    event.stopPropagation();
    if (this.linkedChild) {
      this.descriptionService.open(this.linkedChild.ScorecardId);
    }
  }
}
